import { type ReactNode, useEffect, useRef, useState } from 'react';
import styles from './Table.module.css';

export interface TableColumn {
  /** Matches a key in each row's `cells`. */
  key: string;
  header: string;
  /** Mono face, tabular figures, right-aligned. For amounts and references. */
  numeric?: boolean;
  nowrap?: boolean;
}

export interface TableRow {
  key: string;
  cells: Record<string, ReactNode>;
}

export interface TableProps {
  columns: readonly TableColumn[];
  rows: readonly TableRow[];
  /** Screen-reader description of the table's contents. */
  caption: string;
  emptyMessage?: string;
}

/**
 * Port of components/data/Table.jsx.
 *
 * Two deliberate changes from the source. The source decides whether a cell is
 * numeric by testing its text against /^[$\-+]?[0-9]/, which misses every
 * amount whose currency renders as a code rather than a symbol — "ZAR 58,084.90"
 * would have been left in the body face. Columns declare `numeric` instead.
 *
 * Rows are keyed objects rather than positional arrays, so adding a column
 * cannot silently shift every cell in the table one place to the left.
 *
 * A table wider than its frame scrolls sideways, and a keyboard can only scroll
 * what it can focus (WCAG 2.1.1). The frame becomes a named, focusable region
 * while it overflows, and is not a tab stop when it does not.
 */
export function Table({ columns, rows, caption, emptyMessage = 'Nothing to show.' }: TableProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const frame = scroller.current;
    if (!frame || typeof ResizeObserver === 'undefined') return undefined;
    // Measured when the frame or the table changes size; the observer also
    // reports once on observe, which gives the first answer.
    const observer = new ResizeObserver(() => {
      setOverflowing(frame.scrollWidth > frame.clientWidth);
    });
    observer.observe(frame);
    if (frame.firstElementChild) observer.observe(frame.firstElementChild);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={scroller}
      className={styles.scroller}
      {...(overflowing ? { tabIndex: 0, role: 'region', 'aria-label': caption } : {})}
    >
      <table className={styles.table}>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={column.numeric ? styles.numeric : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className={styles.empty} colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.key}>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={[
                      column.numeric ? styles.numeric : null,
                      column.nowrap ? styles.nowrap : null,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {row.cells[column.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
